import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { BuildingWorkflow, Process } from '@urbanmix-tech/shared-js';
import { useAuth } from '@/shared/context/AuthProvider';

interface ProcessEditModalProps {
  open: boolean;
  onClose: () => void;
  process: Process;
  onSave: (process: Process) => void;
}

function CollapsibleSection({
  label,
  defaultExpanded = false,
  children,
  onRemove,
}: {
  label: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
  onRemove?: () => void;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', flex: 1 }}>
          {label}
        </Typography>
        {onRemove && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <Collapse in={expanded}>
        <Stack sx={{ pl: 3 }}>{children}</Stack>
      </Collapse>
    </Stack>
  );
}

function NestedObjectEditor({
  value,
  onChange,
  depth = 0,
}: {
  value: Record<string, any>;
  onChange: (updated: Record<string, any>) => void;
  depth?: number;
}) {
  const [newKey, setNewKey] = useState('');

  const handleFieldChange = (key: string, fieldValue: any) => {
    onChange({ ...value, [key]: fieldValue });
  };

  const handleRemoveField = (key: string) => {
    const copy = { ...value };
    delete copy[key];
    onChange(copy);
  };

  const handleAddField = () => {
    if (!newKey.trim() || newKey in value) return;
    onChange({ ...value, [newKey.trim()]: '' });
    setNewKey('');
  };

  const renderFieldEditor = (key: string, fieldValue: any) => {
    if (fieldValue !== null && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      return (
        <CollapsibleSection
          key={key}
          label={key}
          defaultExpanded={depth < 1}
          onRemove={() => handleRemoveField(key)}
        >
          <NestedObjectEditor
            value={fieldValue as Record<string, any>}
            onChange={(updated) => handleFieldChange(key, updated)}
            depth={depth + 1}
          />
        </CollapsibleSection>
      );
    }

    if (Array.isArray(fieldValue)) {
      return (
        <CollapsibleSection
          key={key}
          label={`${key} (array)`}
          defaultExpanded={false}
          onRemove={() => handleRemoveField(key)}
        >
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            value={JSON.stringify(fieldValue, null, 2)}
            onChange={(e) => {
              try {
                handleFieldChange(key, JSON.parse(e.target.value));
              } catch {
                // keep raw text while user is typing
              }
            }}
            size="small"
          />
        </CollapsibleSection>
      );
    }

    if (typeof fieldValue === 'boolean') {
      return (
        <Stack key={key} direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
            <InputLabel>{key}</InputLabel>
            <Select
              value={fieldValue ? 'true' : 'false'}
              label={key}
              onChange={(e) => handleFieldChange(key, e.target.value === 'true')}
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => handleRemoveField(key)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }

    if (typeof fieldValue === 'number') {
      return (
        <Stack key={key} direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <TextField
            margin="dense"
            label={key}
            type="number"
            fullWidth
            value={fieldValue}
            onChange={(e) => handleFieldChange(key, Number(e.target.value))}
            size="small"
          />
          <IconButton size="small" onClick={() => handleRemoveField(key)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }

    // string or fallback
    return (
      <Stack key={key} direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <TextField
          margin="dense"
          label={key}
          fullWidth
          value={fieldValue ?? ''}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          size="small"
        />
        <IconButton size="small" onClick={() => handleRemoveField(key)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Stack sx={{ borderLeft: depth > 0 ? '2px solid #333' : 'none', pl: depth > 0 ? 1 : 0 }}>
      {Object.entries(value).map(([key, val]) => renderFieldEditor(key, val))}
      <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 1 }}>
        <TextField
          size="small"
          placeholder="New key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddField();
            }
          }}
        />
        <IconButton size="small" onClick={handleAddField} disabled={!newKey.trim()}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}

const emptyWorkflow: BuildingWorkflow = {
  id: '',
  projectId: '',
  name: '',
  description: '',
  status: 'active',
  webhook_url: '',
  webhook_secret: '',
};

function WorkflowEditor({
  workflow,
  onChange,
  onRemove,
}: {
  workflow: BuildingWorkflow;
  onChange: (updated: BuildingWorkflow) => void;
  onRemove: () => void;
}) {
  const update = <K extends keyof BuildingWorkflow>(key: K, val: BuildingWorkflow[K]) => {
    onChange({ ...workflow, [key]: val });
  };

  return (
    <CollapsibleSection
      label={workflow.name || '(unnamed workflow)'}
      defaultExpanded={false}
      onRemove={onRemove}
    >
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        <TextField
          label="ID"
          size="small"
          fullWidth
          value={workflow.id}
          onChange={(e) => update('id', e.target.value)}
        />
        <TextField
          label="Name"
          size="small"
          fullWidth
          value={workflow.name}
          onChange={(e) => update('name', e.target.value)}
        />
        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={workflow.description}
          onChange={(e) => update('description', e.target.value)}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={workflow.status}
            label="Status"
            onChange={(e) => update('status', e.target.value as 'active' | 'inactive')}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Method</InputLabel>
          <Select
            value={workflow.method ?? ''}
            label="Method"
            onChange={(e) =>
              update('method', (e.target.value || undefined) as BuildingWorkflow['method'])
            }
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Webhook URL"
          size="small"
          fullWidth
          value={workflow.webhook_url}
          onChange={(e) => update('webhook_url', e.target.value)}
        />
        <TextField
          label="Webhook Secret"
          size="small"
          fullWidth
          value={workflow.webhook_secret}
          onChange={(e) => update('webhook_secret', e.target.value)}
        />
        <TextField
          label="Timeout (seconds)"
          size="small"
          type="number"
          fullWidth
          value={workflow.timeout ?? ''}
          onChange={(e) => update('timeout', e.target.value ? Number(e.target.value) : undefined)}
        />
        <TextField
          label="Type"
          size="small"
          fullWidth
          value={workflow.type ?? ''}
          onChange={(e) => update('type', e.target.value || undefined)}
        />
        <TextField
          label="Warning"
          size="small"
          fullWidth
          value={workflow.warning ?? ''}
          onChange={(e) => update('warning', e.target.value || undefined)}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Redirect</InputLabel>
          <Select
            value={workflow.redirect === true ? 'true' : workflow.redirect === false ? 'false' : ''}
            label="Redirect"
            onChange={(e) =>
              update('redirect', e.target.value === '' ? undefined : e.target.value === 'true')
            }
          >
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Parameters (comma-separated)"
          size="small"
          fullWidth
          value={(workflow.parameters ?? []).join(', ')}
          onChange={(e) =>
            update(
              'parameters',
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
        />
      </Stack>
    </CollapsibleSection>
  );
}

export const ProcessEditModal: React.FC<ProcessEditModalProps> = ({
  open,
  onClose,
  process,
  onSave,
}) => {
  const { isEditMode } = useAuth();
  const [editedProcess, setEditedProcess] = useState<Process>({ ...process });

  useEffect(() => {
    if (open) {
      setEditedProcess({
        ...process,
        data: { ...(process.data ?? {}) },
        workflows: process.workflows ? [...process.workflows] : [],
      });
    }
  }, [open, process]);

  const updateField = <K extends keyof Process>(key: K, value: Process[K]) => {
    setEditedProcess((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave(editedProcess);
    onClose();
  };

  // Limited view when not in global edit mode
  if (!isEditMode) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Update Process Status</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              ID: {process.id}
            </Typography>
            <Typography variant="body1">{process.name}</Typography>
            {process.description && (
              <Typography variant="body2" color="text.secondary">
                {process.description}
              </Typography>
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editedProcess.status ?? ''}
                label="Status"
                onChange={(e) => updateField('status', e.target.value)}
              >
                <MenuItem value="start">Start</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Full edit mode
  const workflows = editedProcess.workflows ?? [];

  const updateWorkflow = (index: number, updated: BuildingWorkflow) => {
    const copy = [...workflows];
    copy[index] = updated;
    updateField('workflows', copy);
  };

  const removeWorkflow = (index: number) => {
    updateField(
      'workflows',
      workflows.filter((_, i) => i !== index)
    );
  };

  const addWorkflow = () => {
    updateField('workflows', [
      ...workflows,
      { ...emptyWorkflow, id: `wf_${Date.now()}`, projectId: editedProcess.projectId ?? '' },
    ]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Process</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '70vh' }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            fullWidth
            value={editedProcess.name ?? ''}
            onChange={(e) => updateField('name', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={editedProcess.description ?? ''}
            onChange={(e) => updateField('description', e.target.value)}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={editedProcess.status ?? ''}
              label="Status"
              onChange={(e) => updateField('status', e.target.value)}
            >
              <MenuItem value="start">Start</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Type"
            fullWidth
            value={editedProcess.type ?? ''}
            onChange={(e) => updateField('type', e.target.value)}
          />
          <TextField
            label="Unit ID"
            fullWidth
            value={editedProcess.unitId ?? ''}
            onChange={(e) => updateField('unitId', e.target.value)}
          />
          <TextField
            label="Building ID"
            fullWidth
            type="number"
            value={editedProcess.buildingId ?? ''}
            onChange={(e) =>
              updateField('buildingId', e.target.value ? Number(e.target.value) : undefined)
            }
          />
          <TextField
            label="Webhook URL"
            fullWidth
            value={editedProcess.webhook_url ?? ''}
            onChange={(e) => updateField('webhook_url', e.target.value)}
          />
          <TextField
            label="Webhook Secret"
            fullWidth
            value={editedProcess.webhook_secret ?? ''}
            onChange={(e) => updateField('webhook_secret', e.target.value)}
          />
          <TextField
            label="Parameters (comma-separated)"
            fullWidth
            value={(editedProcess.parameters ?? []).join(', ')}
            onChange={(e) =>
              updateField(
                'parameters',
                e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              )
            }
          />

          <CollapsibleSection label="Data" defaultExpanded={true}>
            <NestedObjectEditor
              value={editedProcess.data ?? {}}
              onChange={(updated) => updateField('data', updated)}
            />
          </CollapsibleSection>

          <CollapsibleSection
            label={`Workflows (${workflows.length})`}
            defaultExpanded={workflows.length > 0}
          >
            <Stack spacing={1}>
              {workflows.map((wf, i) => (
                <WorkflowEditor
                  key={wf.id || i}
                  workflow={wf}
                  onChange={(updated) => updateWorkflow(i, updated)}
                  onRemove={() => removeWorkflow(i)}
                />
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={addWorkflow}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Workflow
              </Button>
            </Stack>
          </CollapsibleSection>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
